/**
 * Thin fetch wrapper around the Express API.
 *
 * In development Vite proxies /api to localhost:4000, so relative URLs work
 * and there is no CORS preflight. In production set VITE_API_URL if the API
 * lives on a different origin than the static site.
 */
const BASE = import.meta.env.VITE_API_URL || '';

const TOKEN_KEY = 'mcog.token';

export const tokenStore = {
  get: () => {
    try {
      return window.localStorage.getItem(TOKEN_KEY);
    } catch {
      // Private browsing or blocked storage — fall back to session-only auth.
      return null;
    }
  },
  set: (token) => {
    try {
      window.localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* ignore */
    }
  },
  clear: () => {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details || [];
  }

  /** Maps server-side field errors into a { field: message } object. */
  get fieldErrors() {
    return this.details.reduce((acc, d) => {
      if (d.field) acc[d.field] = d.message;
      return acc;
    }, {});
  }
}

async function request(path, { method = 'GET', body, auth = true, signal } = {}) {
  const headers = { Accept: 'application/json' };

  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = tokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0
    );
  }

  if (response.status === 204) return null;

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    /* empty or non-JSON body */
  }

  if (!response.ok) {
    const message = payload?.error?.message || 'Something went wrong. Please try again.';
    throw new ApiError(message, response.status, payload?.error?.details);
  }

  return payload;
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  del: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
};
